import type { UserProfile } from '../types/auth.types'

type ProfileIdentity = Pick<
  UserProfile,
  'id' | 'email' | 'firstName' | 'lastName' | 'displayName' | 'avatarUrl' | 'avatarBlurhash'
>

type AuthUserIdentity = {
  id: string
  email?: string | null
  user_metadata?: Record<string, unknown> | null
}

export type CurrentProfileDisplay = {
  id: string | null
  email: string | null
  displayName: string
  firstName: string
  avatarUrl: string | null
  avatarBlurhash: string | null
  fallback: string
  accessibilityLabel: string
}

function cleanText(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const text = value.replace(/\s+/g, ' ').trim()
  return text.length > 0 ? text : null
}

function firstWord(value: unknown): string | null {
  return cleanText(value)?.split(' ')[0] ?? null
}

function joinNames(firstName: string | null, lastName: string | null): string | null {
  const names = [firstName, lastName].filter((value): value is string => Boolean(value))
  return names.length > 0 ? names.join(' ') : null
}

function readMetadata(metadata: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = cleanText(metadata[key])
    if (value) return value
  }
  return null
}

function humanizeEmailLocalPart(email: string | null): string | null {
  if (!email) return null
  const localPart = email
    .split('@')[0]
    ?.replace(/[._-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (!localPart) return null

  return localPart
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toLocaleUpperCase() + part.slice(1))
    .join(' ')
}

export function getCurrentProfileDisplay({
  profile,
  user,
}: {
  profile?: ProfileIdentity | null
  user?: AuthUserIdentity | null
}): CurrentProfileDisplay {
  const metadata = user?.user_metadata ?? {}

  const profileFirstName = cleanText(profile?.firstName)
  const profileLastName = cleanText(profile?.lastName)
  const metadataFirstName =
    readMetadata(metadata, ['first_name', 'firstName', 'given_name', 'givenName']) ??
    firstWord(
      readMetadata(metadata, ['full_name', 'fullName', 'name', 'display_name', 'displayName']),
    )
  const metadataLastName = readMetadata(metadata, [
    'last_name',
    'lastName',
    'family_name',
    'familyName',
  ])
  const email = cleanText(profile?.email) ?? cleanText(user?.email)
  const emailName = humanizeEmailLocalPart(email)

  const displayName =
    cleanText(profile?.displayName) ??
    joinNames(profileFirstName, profileLastName) ??
    readMetadata(metadata, ['full_name', 'fullName', 'name', 'display_name', 'displayName']) ??
    joinNames(metadataFirstName, metadataLastName) ??
    emailName ??
    ''

  const firstName =
    profileFirstName ?? metadataFirstName ?? firstWord(displayName) ?? emailName ?? ''
  const avatarUrl =
    cleanText(profile?.avatarUrl) ??
    readMetadata(metadata, ['avatar_url', 'avatarUrl', 'picture', 'photo_url', 'photoUrl'])
  const fallback = displayName || email || firstName || '?'

  return {
    id: cleanText(profile?.id) ?? cleanText(user?.id),
    email,
    displayName,
    firstName,
    avatarUrl,
    avatarBlurhash: cleanText(profile?.avatarBlurhash),
    fallback,
    accessibilityLabel: displayName || email || firstName || 'Perfil',
  }
}

/**
 * The person's full name as they gave it, or an empty string.
 *
 * There were three of these written inline —
 * `\`${profile?.firstName ?? ''} ${profile?.lastName ?? ''}\`.trim()` in
 * checkout, a display-name-first variant in the organization setup screen, and
 * a third in the AML prefill. Each is a slightly different answer to the same
 * question, which is how a card form ends up empty on a screen that already
 * knows who is paying.
 *
 * Empty rather than a placeholder: a caller offering this as a default needs to
 * know when there is nothing to offer, and 'Cliente' silently accepted into a
 * cardholder field is worse than a field the person fills themselves.
 *
 * @public
 */
export function fullProfileName(profile: Partial<ProfileIdentity> | null | undefined): string {
  const composed = [cleanText(profile?.firstName), cleanText(profile?.lastName)]
    .filter(Boolean)
    .join(' ')
    .trim()
  return composed || cleanText(profile?.displayName) || ''
}
